/**
 * Component map for rendering markdown INLINE — inside a phrasing context
 * (`<legend>`, a label `<p>`, a button title) where a nested block element
 * would be invalid HTML.
 *
 * Every block-level element `MarkdownContent` can emit is flattened to its
 * children. Pass it as `components` together with `as='span'`:
 *
 *   <MarkdownContent content={text} as='span' components={INLINE_MARKDOWN_COMPONENTS} />
 *
 * A caller that needs to flatten more (e.g. links, because the host is itself
 * a `<button>`) extends the map rather than copying it:
 *
 *   const MY_MAP = extendInlineMarkdown('a');
 *
 * Table elements are not covered — no inline caller expects them.
 */

import type { ComponentType, ReactNode } from 'react';

import { InlineText } from './inline-text';

const BLOCK_TAGS = [
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'hr',
] as const;

type InlineMarkdownComponents = Record<
  string,
  ComponentType<{ children?: ReactNode }>
>;

function flattenTags(tags: readonly string[]): InlineMarkdownComponents {
  return Object.fromEntries(tags.map(tag => [tag, InlineText]));
}

export const INLINE_MARKDOWN_COMPONENTS: InlineMarkdownComponents =
  flattenTags(BLOCK_TAGS);

/**
 * The shared map plus `extraTags` flattened the same way. Hoist the result to
 * module scope at the call site so the `components` prop stays a stable
 * reference.
 */
export function extendInlineMarkdown(
  ...extraTags: string[]
): InlineMarkdownComponents {
  return { ...INLINE_MARKDOWN_COMPONENTS, ...flattenTags(extraTags) };
}
